import React, { useEffect, useState, useCallback, memo, useMemo } from "react";
import Meta from "../../lib/meta/Meta";
import {
  ArrowRight,
  Coffee,
  Eye,
  MapPin,
  Martini,
  Moon,
  Star,
  Sun,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useUnified } from "../../contexts/UnifiedContext";
import { useCity } from "../../contexts/CityContext";
import { useHeroSponsor } from "../../hooks/useHeroSponsor";
import SharedHeader from "../shared/SharedHeader";
import { getAPIRoot } from "../../services/api";
import { resolveAccountAvatarDisplay } from "../../utils/photoResolver";
import { useAvatarImageFallback } from "../../hooks/useAvatarImageFallback";

const HERO_IMAGES = ["/hero.webp", "/heroin.webp"];
const COMPANION_IMAGES = ["/sara.jpg", "/companions/rohan.webp"];
const WORK_CAFE_IMAGE = "/work-cafe.jpg";
const PREMIUM_BAR_IMAGE = "/premium-bar.jpg";
const RADAR_BAR_IMAGE = "/radar-bar.png";

const meTimeCards = [
  {
    title: "Find a Bar",
    subtitle: "Solo friendly places",
    icon: Martini,
    accent: "#ffb06e",
    image: PREMIUM_BAR_IMAGE,
    glow: "linear-gradient(180deg, rgba(255,176,110,0.10), rgba(10,10,12,0.16) 38%, rgba(8,8,10,0.96) 100%)",
    link: "/?tab=stag",
  },
  {
    title: "The Radar",
    subtitle: "Find solos near by",
    icon: Eye,
    accent: "#d946ef",
    image: RADAR_BAR_IMAGE,
    glow: "linear-gradient(180deg, rgba(217,70,239,0.10), rgba(12,9,15,0.18) 38%, rgba(8,8,10,0.96) 100%)",
    link: "/?tab=radar",
  },
  {
    title: "Work From Cafe",
    subtitle: "Plugpoints & WiFi",
    icon: Coffee,
    accent: "#7de0cf",
    image: WORK_CAFE_IMAGE,
    glow: "linear-gradient(180deg, rgba(125,224,207,0.09), rgba(8,12,12,0.14) 38%, rgba(8,8,10,0.96) 100%)",
    link: "/?tab=work",
  },
];

const homepageTestimonials = [
  {
    quote:
      "Found my go-to work café in 2 minutes. Plugpoints and solo tables confirmed.",
    author: "Aarav",
    location: "Bengaluru",
    role: "Product Designer",
  },
  {
    quote:
      "Finally an app that understands solo going. No entry drama, no feeling out of place.",
    author: "Nisha",
    location: "Hyderabad",
    role: "Marketing Lead",
  },
  {
    quote:
      "Stepped into an amazing bar and sat at the counter alone. Completely normal.",
    author: "Rohan",
    location: "Mumbai",
    role: "Startup Founder",
  },
  {
    quote:
      "The radar is wild. I knew who was inside before I even walked in the door.",
    author: "Priya",
    location: "Delhi",
    role: "UX Designer",
  },
];

const BeforeAfterSection = memo(function BeforeAfterSection({ visible }) {
  return (
    <section
      id="homepage-contrast-section"
      className={`mt-5 grid grid-cols-2 gap-2.5 transition-all duration-700 ease-out ${visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
    >
      <div className="rounded-[24px] bg-[linear-gradient(180deg,#141215,#0e0c10)] px-4 py-4 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04),0_14px_36px_rgba(0,0,0,0.16)]">
        <p
          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Before STAGSTER
        </p>
        <ul className="mt-3.5 space-y-3 text-[12.5px] leading-[1.45] text-white/50">
          {[
            "No stags allowed",
            "Can't go alone",
            "Solo table? Awkward.",
            "No idea who is inside",
          ].map((item) => (
            <li key={item} className="flex items-center gap-2.5">
              <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-white/[0.04]">
                <span className="block h-[7px] w-[7px] rounded-full border-[1.5px] border-white/20" />
              </span>
              <span className="line-through decoration-white/20">{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative rounded-[24px] bg-[linear-gradient(180deg,#171112,#0d0a0b)] px-4 py-4 text-white shadow-[inset_0_0_0_1px_rgba(255,203,132,0.16),0_18px_44px_rgba(0,0,0,0.18)]">
        <div className="pointer-events-none absolute inset-0 rounded-[24px] bg-[radial-gradient(circle_at_top_right,rgba(255,203,132,0.06),transparent_60%)]" />
        <p
          className="relative text-[10px] font-semibold uppercase tracking-[0.14em] text-[#ffcb84]/84"
          style={{ fontFamily: "var(--font-display)" }}
        >
          With STAGSTER
        </p>
        <ul className="relative mt-3.5 space-y-3 text-[12.5px] leading-[1.45] text-white/90">
          {[
            "Walk in confidently",
            "Solo is completely normal",
            "Solo table, sorted",
            "See who is there, first",
          ].map((item) => (
            <li key={item} className="flex items-center gap-2.5">
              <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#ffcb84]/14 shadow-[inset_0_0_0_1px_rgba(255,203,132,0.2)]">
                <svg
                  width="9"
                  height="7"
                  viewBox="0 0 9 7"
                  fill="none"
                  className="text-[#ffcb84]"
                >
                  <path
                    d="M1 3.5L3.5 6L8 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
});

const HowItWorksSection = memo(function HowItWorksSection() {
  return (
    <section className="mt-5 overflow-hidden rounded-[30px] bg-[linear-gradient(180deg,#0d0b0e,#09090a)] px-5 py-5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06),0_22px_55px_rgba(0,0,0,0.28)]">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/12 to-white/8" />
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/52"
          style={{ fontFamily: "var(--font-display)" }}
        >
          How it works
        </p>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent via-white/12 to-white/8" />
      </div>
      <div className="relative mt-5">
        <div
          className="absolute left-[19px] top-[28px] bottom-[28px] w-px bg-gradient-to-b from-[#ffcb84]/30 via-[#7de0cf]/20 to-[#ff8fb4]/30"
          aria-hidden
        />
        {[
          {
            icon: Martini,
            accent: "#ffcb84",
            title: "Pick your spot",
            desc: "Find solo-friendly bars, cafes, and venues. No minimum billing, no awkward entry.",
            link: "/?tab=stag",
          },
          {
            icon: Coffee,
            accent: "#7de0cf",
            title: "Step out confidently",
            desc: "Know where the plug points are and if there is solo seating before you leave your home.",
            link: "/?tab=work",
          },
          {
            icon: Eye,
            accent: "#ff8fb4",
            title: "Open the Radar",
            desc: "Our bonus feature. See live signals from other solo people natively at your location.",
            link: "/?tab=radar",
          },
        ].map((step, i) => {
          const Icon = step.icon;
          return (
            <button
              key={step.title}
              type="button"
              onClick={() => (window.location.href = step.link)}
              className={`relative flex gap-4 text-left ${i < 2 ? "pb-5" : ""}`}
            >
              <div className="relative z-[1] flex h-[38px] w-[38px] shrink-0 items-center justify-center">
                <div
                  className="absolute inset-0 rounded-[13px] opacity-20 blur-[8px]"
                  style={{ backgroundColor: step.accent }}
                />
                <div
                  className="relative flex h-[38px] w-[38px] items-center justify-center rounded-[13px]"
                  style={{
                    background: `linear-gradient(180deg, ${step.accent}22, ${step.accent}0A)`,
                    boxShadow: `inset 0 0 0 1px ${step.accent}30`,
                  }}
                >
                  <Icon
                    className="h-[17px] w-[17px]"
                    style={{ color: step.accent }}
                    strokeWidth={1.75}
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <p
                  className="text-[13px] font-semibold text-white/90"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {step.title}
                </p>
                <p className="mt-1 text-[12px] leading-[1.55] text-white/50">
                  {step.desc}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
});

function useInterval(length, ms) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setIndex((p) => (p + 1) % length), ms);
    return () => window.clearInterval(id);
  }, [length, ms]);
  return index;
}

const HeroCarousel = memo(function HeroCarousel({
  isAuthenticated,
  userName,
  avatarUrl,
  avatarInitials,
  avatarPending,
  selectedCity,
}) {
  const { showImage: showHeroAvatarImg, onError: onHeroAvatarError } =
    useAvatarImageFallback(avatarUrl || "");
  const heroIndex = useInterval(HERO_IMAGES.length, 4500);
  const directGoogleHref = `${getAPIRoot()}/auth/google/start?next=${encodeURIComponent("/")}`;
  const quickStartHref = "/?tab=stag";
  const firstName = (userName || "").trim().split(/\s+/)[0] || "there";
  return (
    <section className="relative mt-4 overflow-hidden rounded-[26px] bg-[#0a0a0a] shadow-[0_24px_60px_rgba(0,0,0,0.32)]">
      <div className="relative z-[2] px-4 pt-4">
        <div className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap px-1 py-1 text-center">
          <span className="text-[13.5px] font-medium tracking-[0.005em] text-white/72">
            India&apos;s first
          </span>
          <span className="relative inline-flex">
            <span className="absolute inset-x-0 bottom-0 h-[7px] bg-[#cf2f67]/24 blur-[6px]" />
            <span
              className="relative border-b border-[#cf2f67]/60 px-0.5 text-[11.5px] font-bold uppercase tracking-[0.04em] text-[#ff8fb4]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              stag-friendly
            </span>
          </span>
          <span className="text-[13.5px] font-medium tracking-[0.005em] text-white/72">
            app for solo men and women
          </span>
        </div>
      </div>
      <div className="absolute inset-0 z-0" aria-hidden="true">
        {HERO_IMAGES.map((src, idx) => (
          <div
            key={src}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out will-change-[opacity] ${idx === heroIndex ? "opacity-100" : "opacity-0"}`}
            style={{
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transform: "translateZ(0)",
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/22 via-black/10 to-black/56" />
      </div>

      {isAuthenticated ? (
        <div className="hero-logged-in-orbit-layer z-[1]" aria-hidden />
      ) : null}

      <div className="relative z-[2] p-3 pt-2">
        <div className="h-[198px] rounded-[20px]" />
        <div className="pointer-events-none absolute inset-x-3 top-1/2 -translate-y-1/2 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-black/26 px-4 py-1.5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)] backdrop-blur-[2px]">
            <p
              className="text-[11.5px] font-medium tracking-[0.08em] text-[#f7e4c8]/84 [text-shadow:0_1px_2px_rgba(0,0,0,0.65)]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Step out solo. The city is waiting.
            </p>
          </div>
        </div>
        {isAuthenticated && (
          <div className="pointer-events-none absolute inset-x-3 bottom-[72px]">
            <div className="rounded-[18px] bg-[linear-gradient(180deg,rgba(15,13,16,0.88),rgba(8,7,9,0.94))] px-4 py-3 shadow-[0_18px_36px_rgba(0,0,0,0.26),inset_0_0_0_1px_rgba(255,203,132,0.1)] backdrop-blur-xl">
              <div className="flex items-center gap-3">
                {showHeroAvatarImg ? (
                  <img
                    src={avatarUrl}
                    alt={firstName}
                    className="h-11 w-11 shrink-0 rounded-[14px] object-cover shadow-[0_8px_18px_rgba(0,0,0,0.32)]"
                    onError={onHeroAvatarError}
                  />
                ) : (
                  <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#ffcb84]/12 shadow-[inset_0_0_0_1px_rgba(255,203,132,0.16)]">
                    <span
                      className="text-[15px] font-semibold tracking-tight text-[#ffcb84]/95"
                      aria-hidden
                    >
                      {avatarInitials || "?"}
                    </span>
                    {avatarPending ? (
                      <span
                        className="absolute -right-1 -top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-amber-400 text-[11px] font-bold leading-none text-black shadow-[0_1px_4px_rgba(0,0,0,0.45)] ring-1 ring-black/25"
                        title="Photo pending review"
                      >
                        ?
                      </span>
                    ) : null}
                  </div>
                )}
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#ffcb84]/66">
                    You're in
                  </p>
                  <p
                    className="truncate text-[16px] font-semibold text-white"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {`Welcome back, ${firstName}.`}
                  </p>
                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/56">
                    <MapPin
                      className="h-3.5 w-3.5 text-[#ffcb84]/72"
                      strokeWidth={1.8}
                    />
                    <span>{selectedCity || "Your city is set"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-3 mb-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
          <a
            href={isAuthenticated ? quickStartHref : directGoogleHref}
            className="hero-cta-btn group relative z-[20] flex w-full cursor-pointer items-center justify-between overflow-hidden bg-[linear-gradient(180deg,rgba(18,16,20,0.92),rgba(10,8,12,0.97))] px-5 py-3.5 text-[14px] font-semibold text-white backdrop-blur-md transition-all duration-200 ease-out pointer-events-auto active:bg-[linear-gradient(180deg,rgba(18,16,20,1),rgba(10,8,12,1))]"
          >
            <span className="pointer-events-none absolute inset-0 animate-[cta-shimmer_3s_ease-in-out_infinite] bg-[linear-gradient(110deg,transparent_30%,rgba(255,203,132,0.06)_44%,rgba(255,203,132,0.10)_50%,rgba(255,203,132,0.06)_56%,transparent_70%)] bg-[length:200%_100%]" />
            <span className="relative flex items-center gap-3">
              {isAuthenticated ? (
                <span className="flex h-9 w-9 items-center justify-center rounded-[12px] bg-[#ffcb84]/10 shadow-[inset_0_0_0_1px_rgba(255,203,132,0.18)]">
                  <Martini
                    className="h-4.5 w-4.5 text-[#ffcb84]"
                    strokeWidth={1.8}
                  />
                </span>
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-[12px] bg-[linear-gradient(180deg,#fff7ed,#f0e8df)] shadow-[0_2px_8px_rgba(0,0,0,0.2),inset_0_0_0_1px_rgba(0,0,0,0.04)]">
                  <img src="/g-logo.png" alt="" className="h-4.5 w-4.5" />
                </span>
              )}
              <span className="flex flex-col items-start">
                <span className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[#ffcb84]/70">
                  {isAuthenticated
                    ? "Ready when you are"
                    : "Join in under a minute"}
                </span>
                <span className="tracking-[0.005em] text-white/92">
                  {isAuthenticated ? "Explore the city" : "Sign in with Google"}
                </span>
              </span>
            </span>
            <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-[#ffcb84]/10 shadow-[inset_0_0_0_1px_rgba(255,203,132,0.18)]">
              <ArrowRight className="h-3.5 w-3.5 text-[#ffcb84]/70 transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:text-[#ffcb84]" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
});

const TestimonialSection = memo(function TestimonialSection() {
  const testimonialIndex = useInterval(homepageTestimonials.length, 5200);
  const t = homepageTestimonials[testimonialIndex];
  return (
    <section className="mt-5 overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,#0c0b0d,#090909)] px-5 py-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05),0_18px_46px_rgba(0,0,0,0.26)]">
      <p
        className="mb-3 text-center text-[11px] uppercase tracking-[0.14em] text-white/52"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Loved by Solo Goers
      </p>
      <div className="relative rounded-[22px] bg-[linear-gradient(180deg,rgba(255,255,255,0.025),rgba(255,255,255,0.012))] p-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]">
        <div className="absolute right-5 top-2 select-none text-4xl font-serif text-[#ffcb84]/16">
          &ldquo;
        </div>
        <div className="flex items-center gap-1 text-[#ffcb84]/82">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-3.5 w-3.5 fill-current" />
          ))}
        </div>
        <p className="mt-3 text-[15px] leading-7 text-white/88">
          &ldquo;{t.quote}&rdquo;
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[12px]">
          <div className="flex flex-col">
            <span className="text-[15px] font-semibold tracking-[0.03em] text-[#ffcb84]">
              {t.author}
            </span>
            <span className="text-white/56">{t.location}</span>
          </div>
          <span className="text-white/56">{t.role}</span>
        </div>
      </div>
    </section>
  );
});

export default function Homepage2026() {
  const [contrastVisible, setContrastVisible] = useState(false);
  const [dayNightTab, setDayNightTab] = useState("day");
  const [dayNightResetKey, setDayNightResetKey] = useState(0);
  const [, setCompanionImageIndex] = useState(0);
  const { user, isAuthenticated } = useAuth();
  const { unified, photoReviews } = useUnified();
  const { selectedCity } = useCity();
  useHeroSponsor(
    selectedCity || "Hyderabad",
  );
  const heroUserName =
    unified?.user?.full_name || user?.full_name || user?.email || "";
  const heroAvatar = useMemo(
    () =>
      resolveAccountAvatarDisplay({
        user,
        unifiedUser: unified?.user ?? null,
        fridaySwipeProfile: unified?.friday_swipe ?? null,
        photoReviews,
      }),
    [user, unified?.user, unified?.friday_swipe, photoReviews],
  );
  const heroAvatarUrl = heroAvatar.photoUrl;
  const heroAvatarInitials = heroAvatar.initials;
  const heroAvatarPending = heroAvatar.isPendingReview;

  useEffect(() => {
    const companionImageRotationId = window.setInterval(() => {
      setCompanionImageIndex((p) => (p + 1) % COMPANION_IMAGES.length);
    }, 4200);
    return () => {
      window.clearInterval(companionImageRotationId);
    };
  }, []);

  useEffect(() => {
    const dayNightId = window.setInterval(() => {
      setDayNightTab((p) => (p === "day" ? "night" : "day"));
    }, 5000);
    return () => window.clearInterval(dayNightId);
  }, [dayNightResetKey]);

  const handleDayNightClick = useCallback((tab) => {
    setDayNightTab(tab);
    setDayNightResetKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const element = document.getElementById("homepage-contrast-section");
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setContrastVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Meta
        title="STAGSTER - Solo? You're IN."
        description="India's first solo-friendly platform built for stags and solo women."
        canonical="https://www.stagster.com/"
      />
      <link rel="preload" as="image" href="/hero.webp" />
      <link rel="preload" as="image" href="/STAGSTER.webp" />

      <div
        className={`homepage-2026 min-h-screen text-white ${
          isAuthenticated ? "homepage-2026--logged-in" : "bg-black"
        }`}
      >
        <div className={`relative ${isAuthenticated ? "z-[1]" : ""}`}>
          <main className="relative w-full min-h-screen pb-6 pt-0">
            <div className="st-mobile-app-shell">
              <SharedHeader showLocationPicker signalsLiveDemo>
                <>
                  <HeroCarousel
                    isAuthenticated={isAuthenticated}
                    userName={heroUserName}
                    avatarUrl={heroAvatarUrl}
                    avatarInitials={heroAvatarInitials}
                    avatarPending={heroAvatarPending}
                    selectedCity={selectedCity}
                  />
                  {/* HeroSponsorLine hidden for now — sponsor slot reserved, details to be added later */}
                  {/* {!heroSponsorLoading && heroSponsor ? (
                  <HeroSponsorLine
                    label={heroSponsor.label}
                    link={heroSponsor.link}
                    className="mt-2 px-2 pb-0.5"
                  />
                ) : null} */}
                </>
              </SharedHeader>

              <section className="relative mt-5 mb-1 px-0">
                <div className="relative rounded-[28px] bg-[linear-gradient(180deg,#0f0d12,#090909)] px-5 py-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)] overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-fuchsia-400" />
                      </span>
                      <p
                        className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/40"
                        style={{ fontFamily: "var(--font-display)" }}
                      >
                        Radar · Live
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => (window.location.href = "/radar")}
                      className="text-[12px] text-white/30 font-medium hover:text-white/60 transition-colors"
                    >
                      See who's out there →
                    </button>
                  </div>
                  <p className="text-center text-[11px] text-white/20 mt-3">
                    Works best when you're nearby 👀
                  </p>
                </div>
              </section>

              <section className="relative mt-5 overflow-hidden rounded-[28px] px-2 py-3">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.05),transparent_22%),linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]" />
                <div className="flex items-center gap-3 px-1 py-1">
                  <p
                    className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-white/78"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#cf2f67]" />
                    <span className="text-white">ME</span>
                    <span className="text-white/54">TIME?</span>
                  </p>
                </div>
                <div className="relative mt-4 grid grid-cols-3 gap-2.5">
                  {meTimeCards.map((card) => {
                    const Icon = card.icon;
                    const imageSrc =
                      card.title === "The Radar" ? RADAR_BAR_IMAGE : card.image;
                    return (
                      <button
                        key={card.title}
                        type="button"
                        onClick={() => (window.location.href = card.link)}
                        className="group relative overflow-hidden rounded-[24px] bg-[#0b0b0c] text-left transition-all duration-300 ease-out shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05),0_18px_32px_rgba(0,0,0,0.16)] hover:-translate-y-[1px] hover:shadow-[0_22px_42px_rgba(0,0,0,0.22)]"
                      >
                        <img
                          src={imageSrc}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-500"
                        />
                        <div
                          className="absolute inset-0"
                          style={{ background: card.glow }}
                        />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.06),transparent_20%),linear-gradient(180deg,rgba(255,255,255,0.015),rgba(0,0,0,0.12))]" />
                        <div className="absolute inset-0 rounded-[24px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.045)]" />
                        <div
                          className="absolute inset-0 rounded-[24px]"
                          style={{
                            boxShadow: `inset 0 0 0 1px ${card.accent}30, 0 0 14px ${card.accent}12`,
                          }}
                        />
                        <div
                          className="absolute inset-0 rounded-[24px] opacity-90 transition-all duration-300 ease-out group-hover:opacity-100"
                          style={{
                            background: `linear-gradient(120deg, transparent 6%, ${card.accent}16 18%, transparent 30%, transparent 67%, ${card.accent}16 82%, transparent 96%)`,
                            maskImage:
                              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                            WebkitMaskImage:
                              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                            padding: "1px",
                            WebkitMaskComposite: "xor",
                            maskComposite: "exclude",
                          }}
                        />
                        <div
                          className="absolute inset-0 rounded-[24px] opacity-0 blur-[14px] transition-all duration-300 ease-out group-hover:opacity-100"
                          style={{
                            boxShadow: `inset 0 0 0 1px ${card.accent}44, 0 0 28px ${card.accent}26`,
                          }}
                        />
                        <div className="absolute right-0 top-0 z-[1]">
                          <span
                            className="flex h-8 w-8 items-center justify-center rounded-bl-[16px] bg-[linear-gradient(180deg,rgba(0,0,0,0.48),rgba(0,0,0,0.28))] backdrop-blur-[1px] transition-all duration-300 ease-out group-hover:bg-[linear-gradient(180deg,rgba(0,0,0,0.6),rgba(0,0,0,0.34))]"
                            style={{
                              boxShadow: `inset 0 0 0 1px ${card.accent}22, 0 3px 10px rgba(0,0,0,0.24)`,
                            }}
                          >
                            <Icon
                              className="h-4 w-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]"
                              style={{ color: card.accent }}
                              strokeWidth={1.75}
                            />
                          </span>
                        </div>
                        <div className="relative flex h-[156px] flex-col justify-end p-3">
                          <div>
                            <span
                              className="block max-w-[96px] text-[15px] font-semibold leading-[1.14] text-white"
                              style={{ fontFamily: "var(--font-display)" }}
                            >
                              {card.title}
                            </span>
                            <span className="mt-1.5 flex items-center gap-1 text-[10px] leading-none tracking-[-0.01em] text-white/64 whitespace-nowrap">
                              <span className="text-[#99e6b0]">✓</span>
                              <span>{card.subtitle}</span>
                            </span>
                          </div>
                          <span
                            className="absolute inset-x-3 bottom-0 h-px"
                            style={{
                              background: `linear-gradient(90deg, transparent, ${card.accent}38, transparent)`,
                            }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              <BeforeAfterSection visible={contrastVisible} />

              <HowItWorksSection />

              <section className="mt-5 overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,#0c0b0d,#090909)] px-4 py-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05),0_18px_46px_rgba(0,0,0,0.26)]">
                <div className="flex items-center justify-between gap-3">
                  <p
                    className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/56"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Solo-friendly spots
                  </p>
                  <div className="flex rounded-full bg-white/[0.04] p-0.5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]">
                    <button
                      type="button"
                      onClick={() => handleDayNightClick("day")}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium tracking-[0.02em] transition-all duration-200 ${dayNightTab === "day" ? "bg-[#7de0cf]/16 text-[#7de0cf] shadow-[inset_0_0_0_1px_rgba(125,224,207,0.2)]" : "text-white/50 hover:text-white/70"}`}
                    >
                      <Sun className="h-3 w-3" strokeWidth={2} />
                      Day
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDayNightClick("night")}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium tracking-[0.02em] transition-all duration-200 ${dayNightTab === "night" ? "bg-[#ffcb84]/16 text-[#ffcb84] shadow-[inset_0_0_0_1px_rgba(255,203,132,0.2)]" : "text-white/50 hover:text-white/70"}`}
                    >
                      <Moon className="h-3 w-3" strokeWidth={2} />
                      Night
                    </button>
                  </div>
                </div>

                <div className="relative mt-3.5 overflow-hidden rounded-[22px]">
                  <div
                    className={`will-change-[transform,opacity] transition-all duration-300 ease-out ${dayNightTab === "day" ? "translate-x-0 opacity-100" : "-translate-x-full absolute inset-0 opacity-0"}`}
                    style={{
                      transform:
                        dayNightTab === "day"
                          ? "translate3d(0,0,0)"
                          : undefined,
                    }}
                  >
                    <div className="overflow-hidden rounded-[22px] bg-[linear-gradient(180deg,#121417,#0d0f12)] shadow-[inset_0_0_0_1px_rgba(125,224,207,0.08)]">
                      <div className="relative h-[140px] overflow-hidden">
                        <img
                          src={WORK_CAFE_IMAGE}
                          alt="Work cafe"
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.08),rgba(11,15,18,0.86))]" />
                        <div className="absolute inset-x-4 bottom-3">
                          <p
                            className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#7de0cf]"
                            style={{ fontFamily: "var(--font-display)" }}
                          >
                            Work from bar / cafe
                          </p>
                          <p className="mt-1 text-[11px] tracking-[0.02em] text-white/64">
                            Daytime solo spots with Wi-Fi
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-2.5 px-4 py-4 text-[13px] leading-[1.5] text-white/78">
                        {[
                          "Find places with Wi-Fi, plug points, and a secure corner for solo work.",
                          "See happy hour pricing and minimum order before you settle in for the day.",
                          "Pick a cafe or a premium bar where drinking is never mandatory.",
                        ].map((text) => (
                          <li key={text} className="flex items-start gap-2.5">
                            <span className="mt-[3px] flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-[#7de0cf]/12">
                              <svg
                                width="8"
                                height="6"
                                viewBox="0 0 9 7"
                                fill="none"
                              >
                                <path
                                  d="M1 3.5L3.5 6L8 1"
                                  stroke="#7de0cf"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span>{text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div
                    className={`will-change-[transform,opacity] transition-all duration-300 ease-out ${dayNightTab === "night" ? "translate-x-0 opacity-100" : "translate-x-full absolute inset-0 opacity-0"}`}
                    style={{
                      transform:
                        dayNightTab === "night"
                          ? "translate3d(0,0,0)"
                          : undefined,
                    }}
                  >
                    <div className="overflow-hidden rounded-[22px] bg-[linear-gradient(180deg,#171112,#0d0a0b)] shadow-[inset_0_0_0_1px_rgba(255,203,132,0.14)]">
                      <div className="relative h-[140px] overflow-hidden">
                        <img
                          src={PREMIUM_BAR_IMAGE}
                          alt="Premium bar"
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.08),rgba(13,10,11,0.86))]" />
                        <div className="absolute inset-x-4 bottom-3">
                          <p
                            className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#ffcb84]"
                            style={{ fontFamily: "var(--font-display)" }}
                          >
                            Premium bars / pubs for solos
                          </p>
                          <p className="mt-1 text-[11px] tracking-[0.02em] text-white/64">
                            Nightlife that welcomes stags
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-2.5 px-4 py-4 text-[13px] leading-[1.5] text-white/82">
                        {[
                          "Find stag-friendly places where entry feels smoother and more certain.",
                          "See which venues feel gender-neutral and easier for solo women too.",
                          "Find premium pubs that are buzzing but still respectful to solos.",
                        ].map((text) => (
                          <li key={text} className="flex items-start gap-2.5">
                            <span className="mt-[3px] flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-[#ffcb84]/12">
                              <svg
                                width="8"
                                height="6"
                                viewBox="0 0 9 7"
                                fill="none"
                              >
                                <path
                                  d="M1 3.5L3.5 6L8 1"
                                  stroke="#ffcb84"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span>{text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <TestimonialSection />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
